import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/hoobuyData";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isTransparent = !scrolled && isHome;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? "bg-transparent"
          : "bg-white border-b border-foreground-100 shadow-sm"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`text-lg font-bold font-heading tracking-tight whitespace-nowrap cursor-pointer ${
              isTransparent ? "text-white" : "text-foreground-950"
            }`}
          >
            Hoobuy Sheet
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                isTransparent
                  ? "text-white/90 hover:text-white"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Início
            </Link>
            <Link
              to="/hoobuy-spreadsheet-guia"
              className={`text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                isTransparent
                  ? "text-white/90 hover:text-white"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Guia de Compras
            </Link>
            {isHome && (
              <>
                <a
                  href="#categories"
                  className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    isTransparent
                      ? "text-white/80 hover:text-white"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  Categorias
                </a>
                <a
                  href="#comparison"
                  className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    isTransparent
                      ? "text-white/80 hover:text-white"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  Comparativo
                </a>
                <a
                  href="#faq"
                  className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    isTransparent
                      ? "text-white/80 hover:text-white"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  FAQ
                </a>
                <a
                  href="#articles"
                  className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    isTransparent
                      ? "text-white/80 hover:text-white"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  Artigos
                </a>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 ${
                isTransparent
                  ? "bg-white text-foreground-950 hover:bg-white/90"
                  : "bg-primary-500 text-white hover:bg-primary-600"
              }`}
            >
              hoobuy sheet
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                isTransparent
                  ? "text-white hover:bg-white/15"
                  : "text-foreground-950 hover:bg-foreground-100"
              }`}
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white border-t border-foreground-100 px-4 py-4 space-y-2">
          <Link
            to="/"
            className="block px-4 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-foreground-100 cursor-pointer transition-colors"
          >
            <div className="w-5 h-5 inline-flex items-center justify-center mr-3">
              <i className="ri-home-line"></i>
            </div>
            Início
          </Link>
          <Link
            to="/hoobuy-spreadsheet-guia"
            className="block px-4 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-foreground-100 cursor-pointer transition-colors"
          >
            <div className="w-5 h-5 inline-flex items-center justify-center mr-3">
              <i className="ri-book-open-line"></i>
            </div>
            Guia de Compras
          </Link>

          <div className="border-t border-foreground-100 pt-3 mt-3">
            <p className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">
              Categorias
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.searchId}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-foreground-100 hover:text-foreground-950 cursor-pointer transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center px-5 py-3 bg-primary-500 text-white font-semibold text-sm rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
            >
              hoobuy sheet
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}